import { Card, Label, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { getDashColor } from '~/shared/components/icons';
import { useEffect, useState, useContext } from 'react';
import MedicalExpenses from './MedicalExpenses';
import Select from 'react-select';
import TravelExpenses from './TravelExpenses';
import FoodExpense from './FoodExpense';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ShowExpenses from './ExpensesListing';
import { FundRequestContext } from '~/context/fundrequests';
import { CampaignContext } from '~/context/campaign';
import { apiUrl, authHeaders } from '~/services/auth';
import {
    getFundDisbursementGuidelinesUrl,
    getCampaignTypeFromPatient,
} from '../fundDisbursementGuidelines';
import {
    fetchTravelSchema,
    buildTravelDefault,
    travelFieldNames as getTravelFieldNames,
} from '@shared/expenses/travel';

// Helper function to get transaction key from category value
const getTransactionKey = (category) => {
    switch (category) {
        case 'food_incidentals':
            return 'food';
        case 'travel_mileage':
            return 'travel';
        default:
            return category;
    }
};

const AddExpenses = ({ handleSubmission }) => {
    const { control, errors, watch, unregister } = useFormContext();
    const [
        { categories, expenseItem, expenseType, selectedCategory, editMode },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [travelSchema, setTravelSchema] = useState(null);

    useEffect(() => {
        // Load centralized travel schema
        (async () => {
            try {
                const headers = authHeaders();
                const schema = await fetchTravelSchema(apiUrl, headers);
                setTravelSchema(schema);
            } catch (e) {
                console.error('Failed to load travel schema', e);
            }
        })();
    }, []);

    useEffect(() => {
        // Set selectedOption (category)
        if (categories && expenseItem?.id) {
            const index = categories.findIndex(
                (c) => c.id === expenseItem.category
            );
            const itemCategory = categories[index];
            if (!itemCategory) return;
            fundRequestDispatch({
                type: 'set_selected_category',
                payload: itemCategory,
            });
        }
    }, [expenseItem, categories]);

    const fundraisingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_fundraising',
        keyName: '_id',
    });

    const medicalExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_medical',
        keyName: '_id',
    });

    const medicationExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_medications',
        keyName: '_id',
    });

    const insuranceExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_insurance',
        keyName: '_id',
    });

    const relocationLodgingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_relocation_lodging',
        keyName: '_id',
    });

    const travelExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_travel',
        keyName: '_id',
    });

    const foodExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_food',
        keyName: '_id',
    });

    const careGivingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_caregiving',
        keyName: '_id',
    });
    const physicalTherapyExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_physical_therapy',
        keyName: '_id',
    });

    const otherExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_other',
        keyName: '_id',
    });

    const getFieldArrayByCategory = (category) => {
        if (category === 'fundraising') {
            return fundraisingExpensesFieldArray;
        }
        if (category === 'medical') {
            return medicalExpensesFieldArray;
        }
        if (category === 'caregiving') {
            return careGivingExpensesFieldArray;
        }
        if (category === 'physical_therapy') {
            return physicalTherapyExpensesFieldArray;
        }
        if (category === 'medications') {
            return medicationExpensesFieldArray;
        }
        if (category === 'insurance') {
            return insuranceExpensesFieldArray;
        }
        if (category === 'relocation_lodging') {
            return relocationLodgingExpensesFieldArray;
        }
        if (category === 'other') {
            return otherExpensesFieldArray;
        }
        if (category === 'travel_mileage') {
            return travelExpensesFieldArray;
        }
        if (category === 'food_incidentals') {
            return foodExpensesFieldArray;
        }
    };

    const handleCategoryChange = (cat) => {
        console.log('handling category change', cat);

        // Clear previous category's field array and unregister its form fields
        if (selectedCategory) {
            const previousFieldArray = getFieldArrayByCategory(selectedCategory.value);

            if (previousFieldArray && previousFieldArray.fields.length > 0) {
                // Get the index of the last item in this category's array
                const indexToRemove = previousFieldArray.fields.length - 1;

                // Determine the previous form key to unregister
                const prevCategoryKey = getTransactionKey(selectedCategory.value);
                const previousFormKey = `transactions_${prevCategoryKey}[${indexToRemove}]`;

                // Unregister fields to clean up form state
                try {
                    // Unregister based on category type
                    if (selectedCategory.value === 'food_incidentals') {
                        unregister(`${previousFormKey}.recipient_type`);
                        unregister(`${previousFormKey}.recipient_name`);
                        unregister(`${previousFormKey}.start_date`);
                        unregister(`${previousFormKey}.end_date`);
                        unregister(`${previousFormKey}.amount`);
                    } else if (selectedCategory.value === 'travel_mileage') {
                        const names = travelSchema
                            ? getTravelFieldNames(travelSchema)
                            : [
                                  'date_of_travel',
                                  'date_of_travel_end',
                                  'destination',
                                  'miles_driven',
                                  'parking_amount',
                                  'toll_amount',
                                  'car_rental',
                                  'airfare',
                                  'rideshare',
                                  'amount',
                              ];
                        names.forEach((n) => unregister(`${previousFormKey}.${n}`));
                    } else {
                        // For medical and other categories
                        unregister(`${previousFormKey}.expense_date`);
                        unregister(`${previousFormKey}.end_date`);
                        unregister(`${previousFormKey}.amount`);
                        unregister(`${previousFormKey}.description`);
                        unregister(`${previousFormKey}.category`);
                    }
                } catch (e) {
                    console.error('Error unregistering fields:', e);
                }

                // Remove the field from the field array
                if (indexToRemove >= 0) {
                    previousFieldArray.remove(indexToRemove);
                }
            }
        }

        fundRequestDispatch({
            type: 'set_selected_category',
            payload: cat,
        });

        if (!cat) {
            return;
        }
        let newIndex, newFormKey, newItem;

        switch (cat.value) {
            case 'fundraising':
            case 'medical':
            case 'medications':
            case 'insurance':
            case 'relocation_lodging':
            case 'physical_therapy':
            case 'caregiving':
            case 'other':
                {
                    const existingArray = getFieldArrayByCategory(cat.value);
                    if (!existingArray) {
                        console.error('No field array found for category:', cat.value);
                        break;
                    }
                    newIndex = existingArray.fields.length;
                    const transactionKey = getTransactionKey(cat.value);
                    newFormKey = `transactions_${transactionKey}[${newIndex}]`;
                    newItem = {
                        category: cat.id,
                        expense_date: '',
                        end_date: null,
                        amount: 0,
                        description: '',
                    };
                    existingArray.append(newItem);
                    fundRequestDispatch({
                        type: 'set_expense_info',
                        expenseIndex: newIndex,
                        expenseType: 'default',
                        expenseCategory: cat,
                        expenseItem: newItem,
                    });
                }
                break;
            case 'food_incidentals':
                {
                    newIndex = foodExpensesFieldArray.fields.length;
                    newFormKey = `transactions_food[${newIndex}]`;
                    newItem = {
                        recipient_type: null,
                        recipient_name: '',
                        start_date: '',
                        end_date: '',
                        amount: 0,
                    };
                    foodExpensesFieldArray.append(newItem);
                    fundRequestDispatch({
                        type: 'set_expense_info',
                        expenseIndex: newIndex,
                        expenseType: 'food',
                        expenseCategory: cat,
                        expenseItem: newItem,
                    });
                }
                break;
            case 'travel_mileage': {
                newIndex = travelExpensesFieldArray.fields.length;
                newFormKey = `transactions_travel[${newIndex}]`;
                newItem = travelSchema
                    ? buildTravelDefault(travelSchema)
                    : {
                          date_of_travel: '',
                          date_of_travel_end: '',
                          destination: '',
                          miles_driven: 0,
                          parking_amount: 0,
                          toll_amount: 0,
                          car_rental: 0,
                          airfare: 0,
                          rideshare: 0,
                          amount: 0,
                      };
                travelExpensesFieldArray.append(newItem);
                fundRequestDispatch({
                    type: 'set_expense_info',
                    expenseIndex: newIndex,
                    expenseType: 'travel',
                    expenseCategory: cat,
                    expenseItem: newItem,
                });
            }
        }
    };

    const renderExpenseComponent = () => {
        let expenseType = '';

        switch (selectedCategory?.value) {
            case '':
                return <></>;
            case 'travel_mileage':
                expenseType = 'travel';
                return (
                    <TravelExpenses
                        fieldArray={travelExpensesFieldArray}
                        handleSubmission={handleSubmission}
                        travelSchema={travelSchema}
                    />
                );
            case 'food_incidentals':
                expenseType = 'food';
                return (
                    <FoodExpense
                        fieldArray={foodExpensesFieldArray}
                        handleSubmission={handleSubmission}
                    />
                );
            case 'fundraising':
            case 'medical':
            case 'medications':
            case 'insurance':
            case 'physical_therapy':
            case 'caregiving':
            case 'relocation_lodging':
            case 'other':
                expenseType = 'default';
                const fieldArray = getFieldArrayByCategory(selectedCategory.value);
                return (
                    <MedicalExpenses
                        fieldArray={fieldArray}
                        handleSubmission={handleSubmission}
                        categoryValue={selectedCategory.value}
                    />
                );
        }
    };

    // Move 'other' category to the bottom of the list of categories
    const categoriesOptions = categories.filter((x) => x.value !== 'other');
    const otherCategory = categories.find((x) => x.value === 'other');
    if (otherCategory) {
        categoriesOptions.push(otherCategory);
    }

    return (
        <>
            <ShowExpenses
                defaultExpensesFieldArray={medicalExpensesFieldArray}
                foodExpensesFieldArray={foodExpensesFieldArray}
                travelExpensesFieldArray={travelExpensesFieldArray}
                fundraisingExpensesFieldArray={fundraisingExpensesFieldArray}
                medicationExpensesFieldArray={medicationExpensesFieldArray}
                insuranceExpensesFieldArray={insuranceExpensesFieldArray}
                careGivingExpensesFieldArray={careGivingExpensesFieldArray}
                physicalTherapyExpensesFieldArray={
                    physicalTherapyExpensesFieldArray
                }
                relocationLodgingExpensesFieldArray={
                    relocationLodgingExpensesFieldArray
                }
                otherExpensesFieldArray={otherExpensesFieldArray}
            >
                {editMode && (
                    <Card
                        style={{
                            padding: 20,
                            background: getDashColor('supporters'),
                            marginTop: 12,
                        }}
                    >
                        <>
                            <Label>Expense Category</Label>
                            <Select
                                name='category'
                                label='Category'
                                control={control}
                                value={selectedCategory}
                                defaultValue={{ label: 'Select', value: '' }}
                                errors={errors?.category?.message}
                                options={categoriesOptions}
                                onChange={handleCategoryChange}
                                isDisabled={expenseItem?.id !== undefined}
                            />
                            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                                Not sure what category to choose?{' '}
                                <button
                                    type="button"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#17a2b8',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        fontSize: '14px'
                                    }}
                                    onClick={() => setShowCategoryModal(true)}
                                >
                                    Click here to learn about them.
                                </button>
                            </div>
                        </>

                        {selectedCategory && (
                            <div>
                                {expenseItem?.id && (
                                    <div className='mt-4'>
                                        <h5 className='mb-0 text-primary font-weight-bold'>
                                            Editing: {
                                                expenseItem?.description ||
                                                expenseItem?.destination ||
                                                expenseItem?.recipient_name ||
                                                'Expense'
                                            }
                                        </h5>
                                    </div>
                                )}
                                {renderExpenseComponent()}
                            </div>
                        )}
                    </Card>
                )}
            </ShowExpenses>

            <Modal 
                isOpen={showCategoryModal} 
                toggle={() => setShowCategoryModal(false)}
                size="lg"
            >
                <ModalHeader toggle={() => setShowCategoryModal(false)}>
                    <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                        What are the Expense Categories?
                    </h3>
                </ModalHeader>
                <ModalBody>
                    <div style={{ marginBottom: '20px' }}>
                        <p>
                        Not sure which category to choose? No worries, make your best guess and our team will adjust if needed.
                        </p>
                    </div>
                    
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.8', marginBottom: '20px' }}>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Medical</strong> – Hospital, doctors, and lab fees not covered by insurance, including deductibles and copays; phone and TV rentals during hospitalization. Treatments that are FDA-approved or currently under FDA review and recommended by a medical professional. Disposable medical equipment and supplies for the client, including catheters, pads, briefs, and medically necessary OTC drugstore items.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Medications</strong> – Illness or Injury-related prescriptions, over-the-counter medications, and medication co-pays.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Insurance</strong> – Health insurance premiums, including dental and vision, for the client only. COBRA premiums for parents who must take a leave of absence or family medical leave to be a caregiver for a minor client. Leave documentation or FMLA is required. Cannot also request payment for full-time caregiver services.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Travel/Mileage</strong> – Local travel (one-day round trip) and long-distance travel (overnight or longer) for client and one caregiver to access treatment or for caregivers to visit the hospitalized client. Reasonable maintenance costs for a vehicle used for accessing treatments. Airfare, ride-shares, car rental, buses for treatments and appointments.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Relocation/Lodging</strong> – Housing and moving expenses for the client and one caregiver to move to access treatment. Necessary living expenses, such as mortgage payments (P&I only), rent, and utilities up to $2,000 per month.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Food/Incidentals</strong> – Per diem for meals, drinks, and other minor expenses incurred during medical travel or temporary relocation for treatment. For the client and caregiver while the client is outpatient, and for the caregiver only while the client is inpatient.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Physical Therapy</strong> – Medically recommended therapies including physical, occupational, activity-based, speech, and massage; therapeutic recreational camps; adaptive sports.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Caregiving</strong> – One caregiver for any of the following: in-home nursing care, personal care assistants, travel aides, and hospital bedside caregivers. Help Hope Live does not cover childcare expenses.
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                            <strong>Fundraising</strong> – Reasonably priced materials you need to carry out your community-based fundraising activities for Help Hope Live.
                        </li>
                    </ul>
                    
                    <div style={{ marginTop: '20px' }}>
                        <p>
                            You can get a more detailed breakdown with examples by checking out the{' '}
                            <a 
                                href={getFundDisbursementGuidelinesUrl(
                                    getCampaignTypeFromPatient(campaign?.patient)
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#17a2b8', textDecoration: 'underline' }}
                            >
                                Fund Disbursement Guidelines
                            </a>.
                        </p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="dark" 
                        outline
                        onClick={() => setShowCategoryModal(false)}
                        style={{ borderRadius: '50px', padding: '10px 30px' }}
                    >
                        CLOSE
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AddExpenses;
